import React, { useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export const avatarSrc = (u) => (u?.avatar_url ? `${BACKEND_URL}${u.avatar_url}` : u?.picture || undefined);
const initialsOf = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

export const UserAvatar = ({ user, className = "w-9 h-9", fallbackClass = "bg-emerald-600 text-white text-xs" }) => (
  <Avatar className={className}>
    <AvatarImage src={avatarSrc(user)} alt={user?.name} className="object-cover" />
    <AvatarFallback className={fallbackClass}>{initialsOf(user?.name)}</AvatarFallback>
  </Avatar>
);

export const AvatarUploader = ({ user, endpoint, onChange, className = "w-10 h-10", testid = "avatar-upload" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Escolha um arquivo de imagem");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd);
      toast.success("Foto atualizada");
      onChange?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar foto"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  const remove = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.delete(endpoint);
      toast.success("Foto removida");
      onChange?.(data);
    } catch { toast.error("Falha ao remover foto"); }
  };

  return (
    <div className="relative group shrink-0">
      <button type="button" onClick={() => ref.current?.click()} title="Alterar foto" data-testid={testid}
              className="relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
        <UserAvatar user={user} className={className} />
        <span className="absolute inset-0 rounded-full bg-black/45 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        </span>
      </button>
      {user?.avatar_url && (
        <button type="button" onClick={remove} title="Remover foto" data-testid={`${testid}-remove`}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-card border border-border text-muted-foreground hover:text-destructive flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <X className="w-2.5 h-2.5" />
        </button>
      )}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
             onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testid}-input`} />
    </div>
  );
};
